// © Microsoft Corporation. All rights reserved.

import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Stack, Spinner } from '@fluentui/react';
import { LocalPreview } from './LocalPreview';
import { LocalSettings } from './LocalSettings';
import { DisplayNameField } from './DisplayNameField';
import {
  VideoDeviceInfo,
  AudioDeviceInfo,
  LocalVideoStream,
  DeviceManager,
  CallAgent,
  CallEndReason,
  CallClient,
  Call
} from '@azure/communication-calling';
// import { VideoCameraEmphasisIcon } from '@fluentui/react-icons-northstar';
import {
  // videoCameraIconStyle,
  configurationStackTokens,
  // buttonStyle,
  localSettingsContainerStyle,
  mainContainerStyle,
  fullScreenStyle,
  verticalStackStyle
} from './styles/Configuration.styles';
import {
  localStorageAvailable,
  getDisplayFirstNameFromLocalStorage,
  getDisplayLastNameFromLocalStorage
} from '../Utils/localStorage';
import HomeScreen from '../components/HomeScreen';

export interface ConfigurationScreenProps {
  userId: string;
  groupId: string;
  callClient: CallClient;
  callAgent: CallAgent;
  deviceManager: DeviceManager;
  setupCallClient(unsupportedStateHandler: () => void): void;
  setupCallAgent(callClient: CallClient, displayName: string): void;
  startCallHandler(threadId: boolean, status: number, groupId: string): void;
  startCallLoader(loader: boolean): void;
  startCall(loader: boolean): void;
  joinTeamsMeeting(meetingLink: string): void;
  createThreadHandler(groupId: string): void;
  scheduleMeeting(): any;
  unsupportedStateHandler: () => void;
  callEndedHandler: (reason: CallEndReason) => void;
  videoDeviceList: VideoDeviceInfo[];
  audioDeviceList: AudioDeviceInfo[];
  setVideoDeviceInfo(device: VideoDeviceInfo): void;
  setAudioDeviceInfo(device: AudioDeviceInfo): void;
  mic: boolean;
  setMic(mic: boolean): void;
  setLocalVideoStream(stream: LocalVideoStream | undefined): void;
  localVideoRendererIsBusy: boolean;
  videoDeviceInfo: VideoDeviceInfo;
  audioDeviceInfo: AudioDeviceInfo;
  localVideoStream: LocalVideoStream;
  screenWidth: number;
  call: Call;
  localVideo: boolean;
  darkColor: string;
  lightColor: string;
  homeColor: string;
  rejoin: boolean;
  pageLoader: boolean;
  reCall(): any;
  reJoinLoad(): any;
  meetingData: any;
}

export const Configuration = (props: ConfigurationScreenProps): JSX.Element => {
  const spinnerLabel = 'Initializing call client...';
  // const buttonText = 'Start a call';

  // const createUserId = () => 'user' + Math.ceil(Math.random() * 1000);

  const [name, setName] = useState(localStorageAvailable ? (getDisplayFirstNameFromLocalStorage() + ' ' + getDisplayLastNameFromLocalStorage()) : '');
  const [emptyWarning, setEmptyWarning] = useState(false);

  const { setupCallClient, setupCallAgent, unsupportedStateHandler, callClient } = props;
  // console.log(callClient, 'callClient1111111111')
  const memoizedSetupCallClient = useCallback(() => setupCallClient(unsupportedStateHandler), [unsupportedStateHandler]);

  useEffect(() => {
    memoizedSetupCallClient();
  }, [memoizedSetupCallClient]);

  useEffect(() => {
    if (props.localVideo) {
      handleLocalVideoOnOff();
    }
  }, []);

  const handleLocalVideoOnOff = async () => {
    if (props.localVideoStream) {
      await props.call.stopVideo(props.localVideoStream);
      props.setLocalVideoStream(undefined);
    } else {
      if (props.videoDeviceInfo) {
        const localVideoStream = new LocalVideoStream(props.videoDeviceInfo);
        props.setLocalVideoStream(localVideoStream);
        await props.call.startVideo(localVideoStream);
      }
    }
  };

  useEffect(() => {
    if (props.pageLoader && callClient) {
      props.startCallLoader(false);
      reloadpage();
    }
  }, [callClient])

  const reloadpage = async () => {
    const name: string = getDisplayFirstNameFromLocalStorage() + ' ' + getDisplayLastNameFromLocalStorage();
    await setupCallAgent(callClient, name);
    props.reCall()
  }

  useEffect(() => {
    if (props.rejoin && callClient) {
      props.startCallLoader(false);
      reloadpages()
    }
  }, [callClient])

  const reloadpages = async () => {
    // const name: string = getDisplayFirstNameFromLocalStorage() + ' ' + getDisplayLastNameFromLocalStorage();
    // await setupCallAgent(callClient, name);
    props.reJoinLoad()
  }

  return (
    <Fragment>
      <Stack className={mainContainerStyle} horizontalAlign="center" verticalAlign="center" style={{ display: 'none' }}>
        {props.deviceManager ? (
          <Stack
            className={props.screenWidth > 750 ? fullScreenStyle : verticalStackStyle}
            horizontal={props.screenWidth > 750}
            horizontalAlign="center"
            verticalAlign="center"
            tokens={props.screenWidth > 750 ? configurationStackTokens : undefined}
          >
            <LocalPreview
              mic={props.mic}
              setMic={props.setMic}
              setLocalVideoStream={props.setLocalVideoStream}
              videoDeviceInfo={props.videoDeviceInfo}
              audioDeviceInfo={props.audioDeviceInfo}
              localVideoStream={props.localVideoStream}
              videoDeviceList={props.videoDeviceList}
              audioDeviceList={props.audioDeviceList}
            />
            <Stack className={localSettingsContainerStyle}>
              <DisplayNameField setName={setName} name={name} setEmptyWarning={setEmptyWarning} isEmpty={emptyWarning} />
              <div>
                <LocalSettings
                  videoDeviceList={props.videoDeviceList}
                  audioDeviceList={props.audioDeviceList}
                  audioDeviceInfo={props.audioDeviceInfo}
                  videoDeviceInfo={props.videoDeviceInfo}
                  setVideoDeviceInfo={props.setVideoDeviceInfo}
                  setAudioDeviceInfo={props.setAudioDeviceInfo}
                  deviceManager={props.deviceManager}
                />
              </div>
              {/* <div>
              <PrimaryButton
                className={buttonStyle}
                onClick={async () => {
                  if (!name) {
                    setEmptyWarning(true);
                  } else {
                    setEmptyWarning(false);
                    await setupCallAgent(callClient, name);
                    props.startCallHandler();
                  }
                }}
              >
                <VideoCameraEmphasisIcon className={videoCameraIconStyle} size="medium" />
                {buttonText}
              </PrimaryButton>
            </div> */}
            </Stack>
          </Stack>
        ) : (
          <Spinner label={spinnerLabel} ariaLive="assertive" labelPosition="top" />
        )}
      </Stack>
      <HomeScreen
        darkColor={props.darkColor}
        lightColor={props.lightColor}
        homeColor={props.homeColor}
        rejoin={props.rejoin}
        pageLoader={props.pageLoader}
        startCallHandler={async (threadId: boolean, name: string, status: number, groupId: string) => {
          // console.log(callClient, 'callClient')
          await setupCallAgent(callClient, name);
          props.setMic(true);
          if (props.videoDeviceInfo) {
            const stream = new LocalVideoStream(props.videoDeviceInfo);
            props.setLocalVideoStream(stream);
          }
          props.startCallHandler(threadId, status, groupId);
        }}
        scheduleMeeting={() => {
          props.scheduleMeeting();
        }}
        startCallLoader={(loader: boolean) => {
          props.startCallLoader(loader);
        }}
        startCall={(loader: boolean) => {
          props.startCall(loader);
        }}
        joinTeamsMeeting={(meetingUrl: string) => {
          props.joinTeamsMeeting(meetingUrl);
        }}
        setUpAgent={async (name: any) => {
          // await setupCallAgent(callClient, name);
        }}
        reCall={() => {
          props.reCall()
        }}
        meetingData={props.meetingData}
      />
    </Fragment>
  );
};
